/**
 * createBundleHeaderCellReader
 *
 * Builds the accessor both XLSX entry points hand to their sheet builder so a
 * bundle-header row exports what the grid shows: `createArTableExportSnapshot`
 * (email/form workbook) and `useArTableExport` (in-app single table). Decides
 * nothing itself — `resolveBundleHeaderCellValue` owns precedence and
 * `resolveBundleLabel` owns the name; this only maps a resolution onto a sheet
 * value (SYD-2276).
 *
 * Invariants: 'ownCell'/'driver' read `cells[typeId].value`, never `.calcInput`,
 * which carries stale zeros on real header docs; an aggregation with no roll-up
 * exports blank, exactly as the grid renders nothing for it.
 */

import type {
  ArBundleType,
  ArCellValue,
  ArColumnDefinition,
  ArRowData,
  ArRowId,
  TypeId,
} from '#types';
import type { DocEntry } from '@/store/Store.types';
import { resolveBundleHeaderCellValue, resolveBundleLabel } from '@/components/ArTable/bundleHeader';

type BundleHeaderCell =
  | { isBundleHeader: false }
  | { isBundleHeader: true; value: ArCellValue | undefined };

type BundleHeaderCellReader = (rowId: ArRowId, columnId: string) => BundleHeaderCell;

const NOT_BUNDLE_HEADER: BundleHeaderCell = { isBundleHeader: false };

function createBundleHeaderCellReader(params: {
  columns: readonly ArColumnDefinition[];
  rowsMap: ReadonlyMap<ArRowId, DocEntry<ArRowData>>;
  bundleTypeByHeaderRowId: ReadonlyMap<ArRowId, ArBundleType>;
  kitCalcDriverTypeIds: ReadonlySet<TypeId>;
}): BundleHeaderCellReader {
  const columnsByTypeId = new Map(params.columns.map((column) => [column.typeId, column] as const));
  const firstColumnTypeId = params.columns[0]?.typeId;

  return (rowId, columnId) => {
    const bundleType = params.bundleTypeByHeaderRowId.get(rowId);
    if (bundleType === undefined) return NOT_BUNDLE_HEADER;

    const column = columnsByTypeId.get(columnId);
    if (!column) return { isBundleHeader: true, value: undefined };

    const rowData = params.rowsMap.get(rowId)?.data;
    const resolution = resolveBundleHeaderCellValue({
      rowData,
      column,
      bundleType,
      kitCalcDriverTypeIds: params.kitCalcDriverTypeIds,
      isFirstColumn: columnId === firstColumnTypeId,
    });

    switch (resolution.kind) {
      case 'bundleValue':
        return {
          isBundleHeader: true,
          value: resolveBundleLabel({ bundleValue: rowData?.bundleValue, bundleType, rowId }),
        };
      case 'ownCell':
      case 'driver':
        return { isBundleHeader: true, value: rowData?.cells?.[columnId]?.value };
      case 'aggregation':
        return { isBundleHeader: true, value: resolution.value ?? undefined };
      case 'blank':
        return { isBundleHeader: true, value: undefined };
    }
  };
}

export { createBundleHeaderCellReader };
export type { BundleHeaderCell, BundleHeaderCellReader };
